import type { Router } from "vue-router";

/** Routes a visitor reaches without a session: a 401 there is an answer, not a reason to redirect. */
export const PUBLIC_ROUTES = new Set(["home", "signup", "verify-email", "login", "forgot-password", "reset-password", "privacy", "terms", "staff-login"]);

/** The Fraxion staff pages: their sign-in page is /staff/login, never the client one. */
function isStaffPath(path: string): boolean {
  return path === "/staff" || path.startsWith("/staff/");
}

export interface ApiResult<T> {
  status: number;
  body: T;
}

let router: Router | null = null;
let redirectingToLogin = false;

/**
 * A 401 outside the public pages sends the visitor to the sign-in page, once, however many requests
 * fail together. On a staff page the sign-in page is the staff one, and a 403 staff_session_required —
 * a client session where a staff session is needed — leads there too.
 */
export function installUnauthorizedRedirect(appRouter: Router): void {
  router = appRouter;
  appRouter.afterEach((to) => {
    if (to.name === "login" || to.name === "staff-login") {
      redirectingToLogin = false;
    }
  });
}

/**
 * Calls the portal API. The session travels in an httpOnly cookie the browser sends by itself: no
 * token is ever read or kept by this code.
 */
export async function apiRequest<T = Record<string, unknown>>(
  path: string,
  options: { method?: "GET" | "POST"; body?: object } = {},
): Promise<ApiResult<T>> {
  const response = await fetch(path, {
    method: options.method ?? "GET",
    credentials: "same-origin",
    headers: options.body === undefined ? {} : { "Content-Type": "application/json" },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });
  const text = await response.text();
  let body = {} as T;
  try {
    body = (text === "" ? {} : JSON.parse(text)) as T;
  } catch {
    // An answer that is not JSON (a proxy error page) keeps an empty body: the status still speaks
  }
  if (router !== null && !redirectingToLogin) {
    const current = router.currentRoute.value;
    const onStaffPage = isStaffPath(current.path);
    const staffSessionRequired = response.status === 403 && (body as { error?: unknown }).error === "staff_session_required";
    if ((response.status === 401 || (onStaffPage && staffSessionRequired)) && (typeof current.name !== "string" || !PUBLIC_ROUTES.has(current.name))) {
      redirectingToLogin = true;
      await router.push({ name: onStaffPage ? "staff-login" : "login" });
    }
  }
  return { status: response.status, body };
}

/** Error keys that have a message in `auth.errors`; any other answer shows the generic one. */
const KNOWN_ERRORS = new Set([
  "invalid_email",
  "password_too_short",
  "password_too_long",
  "consent_required",
  "invalid_credentials",
  "too_many_requests",
  "unavailable",
]);

/** The error key of a refused request, for the `auth.errors` messages. */
export function errorKey(result: ApiResult<{ error?: string }>): string {
  if (result.status === 429) {
    return "too_many_requests";
  }
  if (result.status === 503) {
    return "unavailable";
  }
  const key = result.body.error;
  return key !== undefined && KNOWN_ERRORS.has(key) ? key : "generic";
}
